"use client";

/**
 * PasswordForm
 * REQ-029: Account Settings - Password change form
 *
 * Form for changing user's password with:
 * - Current password verification
 * - New password complexity requirements
 * - Confirm password matching
 * - Password strength indicator
 * - Rate limiting display (429 response handling)
 */

import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { PasswordStrengthIndicator } from "./password-strength-indicator";

// ==========================================================================
// Schema
// ==========================================================================

export const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain an uppercase letter")
      .regex(/[a-z]/, "Password must contain a lowercase letter")
      .regex(/[0-9]/, "Password must contain a number"),
    confirmPassword: z.string().min(1, "Please confirm your new password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type PasswordFormData = z.infer<typeof passwordSchema>;

// ==========================================================================
// Props
// ==========================================================================

export interface PasswordFormProps {
  /**
   * Callback when form is submitted with valid data.
   * Return `false` to indicate failure (form will NOT be reset).
   * Return `void` / `true` / `undefined` to indicate success (form resets).
   * Throwing an error also prevents form reset.
   */
  onSubmit: (data: PasswordFormData) => Promise<boolean | void>;
  /** Server-side error message (e.g., invalid current password, rate limited) */
  serverError?: string | null;
  /** Test ID for E2E testing */
  "data-testid"?: string;
}

// ==========================================================================
// Component
// ==========================================================================

/**
 * PasswordForm Component
 *
 * Three-field form with current password, new password (with strength meter),
 * and confirm password. Validates locally before calling onSubmit.
 *
 * @example
 * ```tsx
 * <PasswordForm
 *   onSubmit={async (data) => await accountService.changePassword(data)}
 *   serverError={error}
 * />
 * ```
 */
export function PasswordForm({
  onSubmit,
  serverError,
  "data-testid": testId = "password-form",
}: PasswordFormProps): React.JSX.Element {
  const formId = React.useId();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const watchedNewPassword = watch("newPassword");

  const handleFormSubmit = React.useCallback(
    async (data: PasswordFormData) => {
      const result = await onSubmit(data);
      // Only reset when onSubmit signals success (void/undefined/true).
      // If onSubmit returns false, the parent set a serverError and the
      // user should be able to correct their input without losing data.
      if (result !== false) {
        reset();
      }
    },
    [onSubmit, reset]
  );

  return (
    <Card data-testid={testId}>
      <CardHeader
        title="Change Password"
        subheader="Update your password to keep your account secure"
      />
      <CardContent>
        <Box
          component="form"
          id={formId}
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Stack spacing={3}>
            {serverError && (
              <Alert severity="error" data-testid={`${testId}-server-error`}>
                {serverError}
              </Alert>
            )}

            <TextField
              {...register("currentPassword")}
              type="password"
              label="Current Password"
              fullWidth
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              autoComplete="current-password"
              data-testid={`${testId}-current-password-input`}
              slotProps={{
                htmlInput: {
                  "data-testid": `${testId}-current-password-field`,
                },
              }}
            />

            <TextField
              {...register("newPassword")}
              type="password"
              label="New Password"
              fullWidth
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              autoComplete="new-password"
              data-testid={`${testId}-new-password-input`}
              slotProps={{
                htmlInput: {
                  "data-testid": `${testId}-new-password-field`,
                },
              }}
            />

            <PasswordStrengthIndicator password={watchedNewPassword} />

            <TextField
              {...register("confirmPassword")}
              type="password"
              label="Confirm New Password"
              fullWidth
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              autoComplete="new-password"
              data-testid={`${testId}-confirm-password-input`}
              slotProps={{
                htmlInput: {
                  "data-testid": `${testId}-confirm-password-field`,
                },
              }}
            />
          </Stack>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
        <Button
          type="submit"
          form={formId}
          variant="contained"
          disabled={isSubmitting}
          data-testid={`${testId}-submit-btn`}
        >
          {isSubmitting ? "Updating..." : "Update Password"}
        </Button>
      </CardActions>
    </Card>
  );
}
